import { useParamsContext } from '../../contexts/ParamsContext';
import { useAppContext } from '../../contexts/AppContext';
import { ArrowUpDown, Eye, EyeOff, Plus, Printer, Settings } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface Actions {
    type: string;
}

interface ContentActionProps {
    actions: Actions[];
}

export default function ContentActions(options: ContentActionProps) {
    const { setParams, action: actionParam } = useParamsContext();
    const { hiddenValue, setHiddenValue } = useAppContext();

    const renderActionButton = ({
        key,
        icon: Icon,
        label,
        onClick,
        active = false,
        showLabel = false,
        primary = false,
    }: {
        key: number;
        icon: LucideIcon;
        label: string;
        onClick: () => void;
        active?: boolean;
        showLabel?: boolean;
        primary?: boolean;
    }) => (
        <li key={key} className="flex shrink-0 items-center">
            <button
                type="button"
                className={`sf-content-header-button sf-icon-button btn btn-sm h-9 min-h-0 rounded-full p-0 ${primary ? 'is-primary' : ''} ${
                    showLabel ? 'w-9 xl:w-auto xl:gap-2 xl:px-3' : 'btn-circle w-9'
                }`}
                onClick={onClick}
                aria-label={label}
                aria-pressed={active || undefined}
                title={label}
            >
                <Icon className="text-sm" aria-hidden="true" />
                {showLabel && <span className="hidden whitespace-nowrap text-sm font-medium xl:inline">{label}</span>}
            </button>
        </li>
    );

    const parserAction = (action: any, key: number) => {
        switch (action.type) {
            case 'print': {
                return renderActionButton({
                    key,
                    icon: Printer,
                    label: 'Imprimir',
                    active: actionParam === 'print',
                    onClick: () => {
                        setParams({ action: 'print' });
                    },
                });
            }
            case 'hiddeValues': {
                return renderActionButton({
                    key,
                    icon: hiddenValue ? EyeOff : Eye,
                    label: hiddenValue ? 'Mostrar valores' : 'Ocultar valores',
                    onClick: () => {
                        setHiddenValue(!hiddenValue);
                    },
                });
            }
            case 'add':
                return renderActionButton({
                    key,
                    icon: Plus,
                    label: 'Adicionar',
                    showLabel: true,
                    primary: true,
                    active: actionParam === 'add',
                    onClick: () => {
                        setParams({ action: 'add' });
                    },
                });
            case 'order':
                return renderActionButton({
                    key,
                    icon: ArrowUpDown,
                    label: 'Ordenação',
                    active: actionParam === 'order',
                    onClick: () => {
                        setParams({ action: 'order' });
                    },
                });
            case 'settings':
                return renderActionButton({
                    key,
                    icon: Settings,
                    label: 'Configurações',
                    active: actionParam === 'settings',
                    onClick: () => {
                        setParams({ action: 'settings', id: 'geral' });
                    },
                });
            default:
                return <></>;
        }
    };

    const orderedActions = [
        ...options.actions.filter((action) => action.type === 'add'),
        ...options.actions.filter((action) => action.type !== 'add'),
    ];

    return (
        <ul className="flex flex-wrap items-center gap-1.5 sm:gap-2.5">
            {orderedActions.map((action, i) => parserAction(action, i))}
        </ul>
    );
}
